import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, RefreshCw, ShieldCheck, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/app/api";
import { money } from "@/app/pages/commissions/CommissionList";

function StatusTable({ title, data, withAmounts, testId }) {
  if (!data || !Object.keys(data).length) return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5" data-testid={testId}>
      <h2 className="font-display text-lg font-bold text-ink">{title}</h2>
      <p className="mt-2 text-sm text-slatey">No records.</p>
    </section>
  );
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5" data-testid={testId}>
      <h2 className="font-display text-lg font-bold text-ink">{title}</h2>
      <table className="mt-2 w-full text-left text-sm">
        <thead><tr className="text-xs font-bold uppercase text-slatey">
          <th className="py-1.5">Status</th><th className="py-1.5 text-right">Live</th><th className="py-1.5 text-right">TEST</th>
          {withAmounts && <><th className="py-1.5 text-right">Live £</th><th className="py-1.5 text-right">TEST £</th></>}
        </tr></thead>
        <tbody>
          {Object.entries(data).map(([status, bucket]) => (
            <tr key={status} className="border-t border-slate-50">
              <td className="py-1.5 capitalize text-ink">{status.replaceAll("_", " ")}</td>
              <td className="py-1.5 text-right text-slatey">{bucket.live}</td>
              <td className="py-1.5 text-right text-slatey">{bucket.test}</td>
              {withAmounts && <><td className="py-1.5 text-right text-slatey">{money(bucket.amount_live)}</td><td className="py-1.5 text-right text-slatey">{money(bucket.amount_test)}</td></>}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export default function Reporting() {
  const [overview, setOverview] = useState(null);
  const [recon, setRecon] = useState(null);
  const [batches, setBatches] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/admin/reporting/overview").then((r) => setOverview(r.data)).catch(() => setOverview(null));
    api.get("/admin/reporting/reconciliation").then((r) => setRecon(r.data)).catch(() => setRecon(null));
    api.get("/admin/reporting/export-batches").then((r) => setBatches(r.data.batches)).catch(() => setBatches([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const download = async (status, demo) => {
    const key = `${status}-${demo}`;
    setBusy(key);
    try {
      const response = await api.get(`/admin/reporting/commission-export?status=${status}&demo=${demo}`, { responseType: "blob" });
      const records = response.headers["x-export-records"];
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `commission-export-${status}-${demo}-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`Export complete — ${records} record(s) included.`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Export failed");
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="admin-reporting" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-ink">Commercial reporting</h1>
          <p className="mt-1 text-sm text-slatey">Cross-system overview, data reconciliation and finance exports. Live and TEST activity are always separated.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-xs font-bold text-ink hover:border-cobalt" data-testid="reporting-refresh">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {/* Reconciliation */}
      <section className={`rounded-3xl border p-5 ${recon?.healthy ? "border-emerald-200 bg-emerald-50/50" : "border-amber-200 bg-amber-50/50"}`} data-testid="reconciliation-panel">
        <div className="flex items-center gap-2">
          {recon?.healthy ? <ShieldCheck className="h-5 w-5 text-emerald-600" /> : <TriangleAlert className="h-5 w-5 text-amber-600" />}
          <h2 className="font-display text-lg font-bold text-ink">
            Data reconciliation {recon ? (recon.healthy ? "— all checks healthy" : `— ${recon.findings.length} finding(s)`) : "— running…"}
          </h2>
        </div>
        <p className="mt-1 text-xs text-slatey">{recon?.checks_run || 12} cross-system integrity checks (orphans, duplicates, broken links, schedule maths) · {recon?.generated_at?.slice(0, 16).replace("T", " ")}</p>
        {recon?.findings?.length > 0 && (
          <ul className="mt-3 space-y-2">
            {recon.findings.map((finding, index) => (
              <li key={index} className="rounded-2xl bg-white p-3 text-xs" data-testid={`finding-${finding.check}`}>
                <span className={`font-bold ${finding.severity === "high" ? "text-rose-600" : "text-amber-700"}`}>{finding.severity.toUpperCase()}</span>
                <span className="ml-2 font-semibold text-ink">{finding.check.replaceAll("_", " ")}</span>
                <span className="ml-2 text-slatey">{finding.message} ({finding.count})</span>
                {finding.ids?.length > 0 && <p className="mt-1 font-mono text-[10px] text-slatey">{finding.ids.join(", ")}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Exports */}
      <section className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="export-panel">
        <h2 className="font-display text-lg font-bold text-ink">Commission payout export</h2>
        <p className="mt-1 text-xs text-slatey">Per-term CSV for finance. Exported records are marked with the batch reference — a repeat export skips them automatically (forced re-export available via the API).</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {[["payable", "false", "Payable — live"], ["payable", "true", "Payable — TEST"], ["paid", "false", "Paid — live"], ["", "false", "All statuses — live"]].map(([status, demo, label]) => (
            <button key={label} onClick={() => download(status, demo)} disabled={Boolean(busy)} data-testid={`export-${status || "all"}-${demo === "true" ? "test" : "live"}`}
              className="inline-flex items-center gap-2 rounded-full bg-cobalt px-4 py-2.5 text-xs font-bold text-white disabled:opacity-50">
              {busy === key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} {label}
            </button>
          ))}
        </div>
        {batches.length > 0 && (
          <ul className="mt-3 space-y-1 text-xs text-slatey" data-testid="export-history">
            {batches.slice(0, 5).map((batch) => (
              <li key={batch.id}>Batch {batch.id} · {batch.record_count} record(s) · {batch.status_filter || "all"} · {batch.demo_filter === "true" ? "TEST" : batch.demo_filter === "false" ? "live" : "all"} · {batch.by} · {batch.at?.slice(0, 16).replace("T", " ")}</li>
            ))}
          </ul>
        )}
      </section>

      {/* Overview tables */}
      {overview && (
        <div className="grid gap-5 lg:grid-cols-2">
          <StatusTable title="Applications" data={overview.applications} testId="report-applications" />
          <StatusTable title="Partner submissions" data={overview.submissions} testId="report-submissions" />
          <StatusTable title="Commissions" data={overview.commissions} withAmounts testId="report-commissions" />
          <StatusTable title="Affiliate rewards" data={overview.rewards} withAmounts testId="report-rewards" />
          <StatusTable title="Affiliate referrals" data={overview.referrals} testId="report-referrals" />
          <StatusTable title="MyAE entitlements" data={overview.entitlements} testId="report-entitlements" />
          <StatusTable title="Affiliates" data={overview.affiliates} testId="report-affiliates" />
          <StatusTable title="Call requests" data={overview.call_requests} testId="report-call-requests" />
        </div>
      )}
      {overview && (
        <p className="text-xs text-slatey" data-testid="report-totals">
          Partners: {overview.partners.active} active / {overview.partners.total} total · Providers: {overview.providers.total} ·
          Commission paid — live {money(overview.commission_paid.live)} / TEST {money(overview.commission_paid.test)}
        </p>
      )}
    </div>
  );
}
